import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { GroupNotification } from '@/types';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

type Notification = {
  id: string;
  tipo: string;
  titulo: string;
  mensagem: string;
  created_at: string;
  read_at: string | null;
  group_id?: string;
  badge_id?: string;
  source: 'group' | 'user';
};

const DISPLAYED_KEY = 'displayed_badge_notifications';

export function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    
    // Subscribe to realtime notifications
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'group_notifications'
        },
        () => {
          fetchNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications'
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar notificações de grupo
      const { data: groupNotifs } = await supabase
        .from('group_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      // Buscar notificações de usuário (badges)
      const { data: userNotifs } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      // Combinar e ordenar
      const combined: Notification[] = [
        ...(groupNotifs || []).map(n => ({ ...n, source: 'group' as const })),
        ...(userNotifs || []).map(n => ({ ...n, source: 'user' as const })),
      ].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ).slice(0, 10);

      setNotifications(combined);
      setUnreadCount(combined.filter(n => !n.read_at).length);
      
      // Toast para novos badges (apenas os que não foram exibidos nesta sessão)
      const displayedIds = JSON.parse(sessionStorage.getItem(DISPLAYED_KEY) || '[]');
      const newBadges = userNotifs?.filter(n => 
        !n.read_at && 
        n.tipo === 'badge_earned' &&
        !displayedIds.includes(n.id)
      ) || [];
      
      if (newBadges.length > 0) {
        newBadges.forEach(badge => {
          toast.success(badge.titulo, {
            description: badge.mensagem.substring(0, 100) + '...',
            duration: 5000,
          });
        });
        
        // Marcar como exibidas nesta sessão
        const newDisplayedIds = [...displayedIds, ...newBadges.map(b => b.id)];
        sessionStorage.setItem(DISPLAYED_KEY, JSON.stringify(newDisplayedIds));
        
        // Marcar como lidas no banco após 2 segundos
        setTimeout(() => {
          newBadges.forEach(badge => {
            markAsRead(badge.id, 'user');
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId: string, source: 'group' | 'user') => {
    try {
      const table = source === 'group' ? 'group_notifications' : 'user_notifications';
      const { error } = await supabase
        .from(table)
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await Promise.all([
        supabase
          .from('group_notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .is('read_at', null),
        supabase
          .from('user_notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .is('read_at', null),
      ]);

      setNotifications(prev =>
        prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success('Todas notificações marcadas como lidas');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    await markAsRead(notification.id, notification.source);
    
    // Navegar baseado no tipo de notificação
    if (notification.badge_id && notification.source === 'user') {
      // Notificação de badge - ir para perfil com query param para abrir dialog automaticamente
      navigate(`/perfil?badge=${notification.badge_id}`);
    } else if (notification.source === 'group' && notification.group_id) {
      // Notificação de grupo
      navigate(`/grupo/${notification.group_id}`);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notificações</span>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <>
                <Badge variant="secondary">{unreadCount} nova{unreadCount !== 1 && 's'}</Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={markAllAsRead}
                  className="h-auto py-1 px-2 text-xs"
                >
                  Marcar todas
                </Button>
              </>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Nenhuma notificação
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start justify-between w-full">
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${!notification.read_at ? 'font-semibold' : ''}`}>
                      {notification.titulo}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notification.mensagem}
                    </p>
                  </div>
                  {!notification.read_at && (
                    <div className="h-2 w-2 bg-primary rounded-full ml-2 mt-1" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(notification.created_at), {
                      addSuffix: true,
                      locale: ptBR
                    })}
                  </span>
                  {notification.badge_id && (
                    <Badge variant="default" className="text-xs">
                      🏆 Badge
                    </Badge>
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
